import { useEffect, useState } from "react";
import { Loader2, UserCheck } from "lucide-react";
import { toast } from "sonner";
import CompanionPresence from "@/components/admin/CompanionPresence";
import MultiDayPresence from "@/components/admin/MultiDayPresence";
import TimeslotSchedule from "@/components/admin/TimeslotSchedule";
import { useAuth } from "@/lib/auth";

export default function AdminPresence() {
  const { authAxios } = useAuth();
  const [events, setEvents] = useState([]);
  const [eventId, setEventId] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await authAxios.get("/admin/events");
        if (cancelled) return;
        const items = (data.items || []).filter((e) => e.companion_app_enabled || e.multi_day_registration_enabled || e.timeslot_booking_enabled);
        setEvents(items);
        setEventId((current) => items.some((event) => event.id === current) ? current : items[0]?.id || "");
      } catch (error) {
        if (!cancelled) toast.error(error?.response?.data?.detail || error.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [authAxios]);

  const selectedEvent = events.find((event) => event.id === eventId);

  return (
    <div data-testid="admin-presence-page">
      <div className="flex items-center gap-3 mb-6">
        <div className="h-11 w-11 rounded-xl bg-gradient-to-br from-svj-orange via-svj-pink to-svj-purple flex items-center justify-center">
          <UserCheck className="h-5 w-5 text-white" />
        </div>
        <div>
          <h1 className="text-2xl font-extrabold text-white">Presentie</h1>
          <p className="text-sm text-slate-400">Live overzicht van deelnemers per dag.</p>
        </div>
      </div>

      {loading && (
        <div className="rounded-2xl border border-svj-border bg-svj-card p-6 text-sm text-slate-400 inline-flex items-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin" /> Events laden…
        </div>
      )}

      {!loading && events.length === 0 && (
        <div className="rounded-2xl border border-svj-border bg-svj-card p-6 text-sm text-slate-400">
          Er zijn nog geen events waarvoor StapKompas of meerdaagse aanmelding actief is.
        </div>
      )}

      {!loading && events.length > 0 && (
        <div className="space-y-5">
          {events.length > 1 && (
            <label className="block max-w-xl">
              <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-400">Event</span>
              <select
                value={eventId}
                onChange={(event) => setEventId(event.target.value)}
                data-testid="presence-event-select"
                className="mt-1 w-full h-11 px-3 rounded-lg bg-svj-card border border-svj-border text-white text-sm focus:outline-none focus:ring-2 focus:ring-svj-cyan"
              >
                {events.map((event) => (
                  <option key={event.id} value={event.id}>{event.title}</option>
                ))}
              </select>
            </label>
          )}

          <div className="rounded-2xl border border-svj-border bg-svj-card p-5 md:p-6">
            {selectedEvent && (
              <div className="mb-5 pb-4 border-b border-svj-border">
                <div className="text-lg font-bold text-white">{selectedEvent.title}</div>
                {selectedEvent.location && <div className="text-xs text-slate-400 mt-1">{selectedEvent.location}</div>}
              </div>
            )}
            {selectedEvent?.timeslot_booking_enabled ? (
              <TimeslotSchedule
                eventId={eventId}
                authAxios={authAxios}
                timeslotManualBookingsEnabled={!!selectedEvent?.timeslot_manual_bookings_enabled}
              />
            ) : selectedEvent?.companion_app_enabled ? (
              <CompanionPresence eventId={eventId} authAxios={authAxios} />
            ) : (
              <MultiDayPresence eventId={eventId} authAxios={authAxios} startNumbersEnabled={!!selectedEvent?.start_numbers_enabled} />
            )}
          </div>
        </div>
      )}
    </div>
  );
}
